import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useTheme } from '../contexts/ThemeContext';
import { DeepSeekService } from '../services/deepSeekService';
import { checkSubscriptionStatus } from '../services/subscriptionService';
import { getNutritionistBranding, uploadNutritionistLogo, deleteNutritionistLogo } from '../services/nutritionistLogoService';
import { PlanUpgrade } from '../components/subscription/PlanUpgrade';
import './Pages.css';

export const Settings: React.FC = () => {
  const { user, role } = useAuth();
  const { subscription } = useSubscription();
  const { theme, setThemeMode, isDark: isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [deepSeekStatus, setDeepSeekStatus] = useState<'checking' | 'configured' | 'missing' | 'error'>('checking');
  const [checkingDeepSeek, setCheckingDeepSeek] = useState(false);
  const [testingDeepSeek, setTestingDeepSeek] = useState(false);
  const [deepSeekConnectionMessage, setDeepSeekConnectionMessage] = useState<string | null>(null);
  const [deepSeekConnectionOk, setDeepSeekConnectionOk] = useState<boolean | null>(null);
  const [daysUntilRenewal, setDaysUntilRenewal] = useState<number | null>(null);

  // Logo Management State
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  const refreshDeepSeekStatus = useCallback(async () => {
    setCheckingDeepSeek(true);
    try {
      const status = await new DeepSeekService().getConfigurationStatus();
      setDeepSeekStatus(status.configured ? 'configured' : 'missing');
    } catch (error) {
      console.error('Falha ao consultar a configuração DeepSeek:', error);
      setDeepSeekStatus('error');
    } finally {
      setCheckingDeepSeek(false);
    }
  }, []);

  const testDeepSeekConnection = useCallback(async () => {
    setTestingDeepSeek(true);
    setDeepSeekConnectionMessage(null);
    setDeepSeekConnectionOk(null);
    try {
      const result = await new DeepSeekService().testConnection();
      setDeepSeekConnectionOk(result.ok);
      setDeepSeekConnectionMessage(result.ok
        ? 'Conexão com a DeepSeek validada.'
        : result.message || 'Não foi possível validar a conexão com a DeepSeek.');
    } finally {
      setTestingDeepSeek(false);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    if (!user?.uid) return;

    try {
      setLoading(true);

      await refreshDeepSeekStatus();
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, refreshDeepSeekStatus]);

  const loadLogo = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const branding = await getNutritionistBranding(user.uid);
      if (branding?.logoUrl) {
        setLogoUrl(branding.logoUrl);
      }
    } catch (error) {
      console.error('Erro ao carregar logo:', error);
    }
  }, [user?.uid]);

  const loadSubscriptionInfo = useCallback(async () => {
    if (!user?.uid) return;
    try {
      await checkSubscriptionStatus(user.uid);
    } catch (error) {
      console.error('Erro ao carregar informações da assinatura:', error);
    }
  }, [user?.uid]);

  const calculateDaysUntilRenewal = useCallback(() => {
    if (!subscription) {
      setDaysUntilRenewal(null);
      return;
    }

    const now = new Date();
    let renewalDate: Date | null = null;

    // Para assinaturas ativas, usar subscriptionEndDate ou nextPaymentDate
    if (subscription.status === 'active') {
      if (subscription.nextPaymentDate) {
        renewalDate = new Date(subscription.nextPaymentDate);
      } else if (subscription.subscriptionEndDate) {
        renewalDate = new Date(subscription.subscriptionEndDate);
      }
    } else if (subscription.status === 'trial' && subscription.trialEndDate) {
      renewalDate = new Date(subscription.trialEndDate);
    } else if (subscription.status === 'cancelled' && subscription.accessEndsAt) {
      renewalDate = new Date(subscription.accessEndsAt);
    }

    if (renewalDate) {
      const diffTime = renewalDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setDaysUntilRenewal(Math.max(0, diffDays));
    } else {
      setDaysUntilRenewal(null);
    }
  }, [subscription]);

  useEffect(() => {
    if (user && role === 'nutritionist') {
      loadSettings();
      loadLogo();
      loadSubscriptionInfo();
    }
  }, [user, role, loadSettings, loadLogo, loadSubscriptionInfo]);

  useEffect(() => {
    if (user && role === 'nutritionist' && subscription) {
      calculateDaysUntilRenewal();
    }
  }, [user, role, subscription, calculateDaysUntilRenewal]);

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('A imagem deve ter no máximo 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('Por favor, selecione uma imagem válida');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async () => {
    if (!user?.uid || !logoFile) {
      alert('Por favor, selecione uma imagem');
      return;
    }

    try {
      setLoading(true);
      const url = await uploadNutritionistLogo(user.uid, logoFile);
      setLogoUrl(url);
      setLogoFile(null);
      setLogoPreview(null);
      alert('Logo enviado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao fazer upload do logo:', error);
      alert(error.message || 'Erro ao fazer upload do logo');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!user?.uid) return;
    if (!window.confirm('Tem certeza que deseja remover o logo?')) {
      return;
    }

    try {
      setLoading(true);
      await deleteNutritionistLogo(user.uid);
      setLogoUrl(null);
      setLogoFile(null);
      setLogoPreview(null);
      alert('Logo removido com sucesso!');
    } catch (error: any) {
      console.error('Erro ao remover logo:', error);
      alert(error.message || 'Erro ao remover logo');
    } finally {
      setLoading(false);
    }
  };

  const getSubscriptionStatusText = () => {
    if (!subscription) return 'Sem assinatura';

    switch (subscription.status) {
      case 'trial': return 'Período de Teste';
      case 'active': return 'Assinatura Ativa';
      case 'cancelled': return 'Assinatura Cancelada';
      case 'expired': return 'Assinatura Expirada';
      default: return 'Desconhecido';
    }
  };

  const getPlanName = () => {
    if (!subscription?.planType) return 'Nenhum';

    const planNames: Record<string, string> = {
      basic: 'Básico',
      medium: 'Médio',
      advanced: 'Avançado',
      monthly: 'Mensal'
    };

    return planNames[subscription.planType] || subscription.planType;
  };

  if (role !== 'nutritionist') {
    return (
      <div className="ns-page">
        <h1>Configurações</h1>
        <p>Apenas nutricionistas podem acessar estas configurações.</p>
      </div>
    );
  }

  return (
    <div className="ns-page">
      <h1>Configurações</h1>
      <p>Gerencie a aparência e as integrações do NutriSync.</p>

      {/* Configurações de Tema */}
      <div style={{ marginTop: '32px' }}>
        <h2 style={{ marginBottom: '16px', fontSize: '20px', fontWeight: 600 }}>Aparência</h2>
        <p style={{ marginBottom: '16px', color: theme.colors.textMuted, fontSize: '14px' }}>
          Escolha entre tema claro, escuro ou automático (segue as configurações do sistema).
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '600px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', flex: 1 }}>
              <input
                type="radio"
                name="theme"
                value="light"
                checked={theme.mode === 'light'}
                onChange={() => setThemeMode('light')}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 500 }}>🌞 Claro</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', flex: 1 }}>
              <input
                type="radio"
                name="theme"
                value="dark"
                checked={theme.mode === 'dark'}
                onChange={() => setThemeMode('dark')}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 500 }}>🌙 Escuro</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', flex: 1 }}>
              <input
                type="radio"
                name="theme"
                value="auto"
                checked={theme.mode === 'auto'}
                onChange={() => setThemeMode('auto')}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 500 }}>⚙️ Automático</span>
            </label>
          </div>

          <div style={{
            padding: '12px',
            background: theme.colors.surface,
            borderRadius: '8px',
            border: `1px solid ${theme.colors.border}`,
            fontSize: '13px',
            color: theme.colors.textMuted
          }}>
            💡 <strong>Dica:</strong> O modo automático segue as configurações do seu sistema operacional.
            {theme.mode === 'auto' && (
              <span style={{ display: 'block', marginTop: '4px' }}>
                Tema atual: {isDarkMode ? 'Escuro' : 'Claro'} (baseado no sistema)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Configuração central da IA */}
      <div style={{ marginTop: '32px' }}>
        <h2 style={{ marginBottom: '16px', fontSize: '20px', fontWeight: 600, color: theme.colors.text }}>IA DeepSeek</h2>
        <p style={{ marginBottom: '16px', color: theme.colors.textMuted, fontSize: '14px' }}>
          A geração de planos usa uma chave única guardada no servidor. Ela não é exibida nem salva no navegador.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '600px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} aria-live="polite">
            <span style={{ fontSize: '14px', color: theme.colors.textMuted }}>Status:</span>
            <strong style={{ fontSize: '14px', color: deepSeekStatus === 'configured' ? 'var(--theme-success)' : deepSeekStatus === 'error' ? 'var(--theme-danger)' : 'var(--theme-warning)' }}>
              {checkingDeepSeek ? 'Consultando servidor…' : deepSeekStatus === 'configured' ? 'Chave presente no servidor' : deepSeekStatus === 'missing' ? 'Aguardando chave no servidor' : 'Não foi possível consultar o servidor'}
            </strong>
          </div>
          <div style={{ padding: '12px', background: theme.colors.surface, borderRadius: '8px', border: `1px solid ${theme.colors.border}`, fontSize: '13px', color: theme.colors.textMuted, lineHeight: 1.6 }}>
            Para configurar, abra o arquivo privado <code>nutrisync-payment-config.php</code> na Hostinger, fora de <code>public_html</code>, e preencha a constante <code>NUTRISYNC_DEEPSEEK_API_KEY</code>. A chave não deve ser colada nesta tela.
          </div>
          <button type="button" onClick={testDeepSeekConnection} disabled={testingDeepSeek || deepSeekStatus === 'missing'} className="ns-btn ns-btn-secondary">
            {testingDeepSeek ? 'Testando conexão…' : 'Testar conexão com DeepSeek'}
          </button>
          {deepSeekConnectionMessage && (
            <div role="status" aria-live="polite" style={{ color: deepSeekConnectionOk ? 'var(--theme-success)' : 'var(--theme-danger)', fontSize: '14px' }}>
              {deepSeekConnectionMessage}
            </div>
          )}
        </div>
      </div>

      {/* Gerenciamento de Logo/Branding */}
      <div style={{ marginTop: '48px', borderTop: `1px solid ${theme.colors.border}`, paddingTop: '32px' }}>
        <h2 style={{ marginBottom: '16px', fontSize: '20px', fontWeight: 600, color: theme.colors.text }}>Logo Personalizado</h2>
        <p style={{ marginBottom: '24px', color: theme.colors.textMuted, fontSize: '14px' }}>
          Faça upload do seu logo para personalizar as imagens geradas para a comunidade. O logo aparecerá de forma discreta nas imagens.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '600px' }}>
          {/* Preview do logo atual */}
          {(logoUrl || logoPreview) && (
            <div style={{
              padding: '20px',
              background: theme.colors.surface,
              borderRadius: '12px',
              border: `1px solid ${theme.colors.border}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{ fontSize: '13px', color: theme.colors.textMuted, fontWeight: 500 }}>Preview do Logo</div>
              <div style={{
                width: '200px',
                height: '200px',
                border: `2px dashed ${theme.colors.border}`,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: theme.colors.surface,
                overflow: 'hidden'
              }}>
                <img
                  src={logoPreview || logoUrl || ''}
                  alt="Logo preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              </div>
            </div>
          )}

          {/* Upload de novo logo */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: theme.colors.text }}>
              {logoUrl ? 'Substituir Logo' : 'Enviar Logo'}
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoSelect}
              style={{ fontSize: '13px', color: theme.colors.text }}
            />
            <p style={{ fontSize: '12px', color: theme.colors.textMuted, marginTop: '8px' }}>
              Formatos aceitos: PNG, JPG, SVG. Tamanho máximo: 5MB
            </p>
          </div>

          {/* Botões de ação */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {logoFile && (
              <button
                type="button"
                onClick={handleLogoUpload}
                disabled={loading}
                className="ns-btn ns-btn-primary"
              >
                {loading ? 'Enviando...' : 'Salvar Logo'}
              </button>
            )}
            {logoUrl && (
              <button
                type="button"
                onClick={handleLogoDelete}
                disabled={loading}
                className="ns-btn"
                style={{ background: 'var(--theme-danger-solid)', color: 'white' }}
              >
                {loading ? 'Removendo...' : 'Remover Logo'}
              </button>
            )}
          </div>

          <div style={{
            padding: '12px',
            background: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : '#eff6ff',
            borderRadius: '8px',
            border: `1px solid ${isDarkMode ? 'rgba(59, 130, 246, 0.3)' : '#bfdbfe'}`,
            fontSize: '13px',
            color: isDarkMode ? '#93c5fd' : '#1e40af'
          }}>
            💡 <strong>Dica:</strong> Use um logo com fundo transparente (PNG) para melhor resultado. O logo aparecerá discretamente no canto inferior direito das imagens geradas.
          </div>
        </div>
      </div>

      {/* Informações da Assinatura */}
      <div className="ns-subscription-section">
        <h2>Assinatura</h2>
        <p>
          Visualize informações sobre sua assinatura e quando ela será renovada.
        </p>

        <div className="ns-subscription-card">
          <div className="ns-subscription-info">
            <div className="ns-subscription-info-item">
              <span className="ns-subscription-info-label">Status da Assinatura</span>
              <span className={`ns-subscription-status-badge ${subscription?.status === 'trial' ? 'status-trial' :
                subscription?.status === 'cancelled' ? 'status-cancelled' :
                  subscription?.status === 'expired' ? 'status-expired' : ''
                }`}>
                {getSubscriptionStatusText()}
              </span>
            </div>

            {subscription?.planType && (
              <div className="ns-subscription-info-item">
                <span className="ns-subscription-info-label">Plano Atual</span>
                <span className="ns-subscription-plan-badge">
                  {getPlanName()}
                </span>
              </div>
            )}

            {daysUntilRenewal !== null && (
              <div className={`ns-subscription-days-counter ${daysUntilRenewal <= 7 ? 'danger' :
                daysUntilRenewal <= 15 ? 'warning' : ''
                }`}>
                <span className="ns-subscription-days-label">Dias para Renovação</span>
                <span className={`ns-subscription-days-value ${daysUntilRenewal <= 7 ? 'danger' :
                  daysUntilRenewal <= 15 ? 'warning' : ''
                  }`}>
                  {daysUntilRenewal} {daysUntilRenewal === 1 ? 'dia' : 'dias'}
                </span>
                {daysUntilRenewal === 0 && (
                  <p className="ns-subscription-days-message danger">
                    Sua assinatura expira hoje. Renove para continuar usando o serviço.
                  </p>
                )}
                {daysUntilRenewal > 0 && daysUntilRenewal <= 7 && (
                  <p className="ns-subscription-days-message warning">
                    Sua assinatura expira em breve. Considere renovar para não perder o acesso.
                  </p>
                )}
              </div>
            )}

            {subscription?.subscriptionStartDate && (
              <div className="ns-subscription-info-item">
                <span className="ns-subscription-info-label">Data de Início</span>
                <span className="ns-subscription-date">
                  {new Date(subscription.subscriptionStartDate).toLocaleDateString('pt-BR')}
                </span>
              </div>
            )}

            {(subscription?.subscriptionEndDate || subscription?.trialEndDate) && (
              <div className="ns-subscription-info-item">
                <span className="ns-subscription-info-label">
                  {subscription.status === 'trial' ? 'Fim do Período de Teste' : 'Data de Expiração'}
                </span>
                <span className="ns-subscription-date">
                  {new Date(subscription.subscriptionEndDate || subscription.trialEndDate).toLocaleDateString('pt-BR')}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upgrade de Plano */}
      <div style={{ marginTop: '48px', borderTop: '1px solid #e2e8f0', paddingTop: '32px' }}>
        <h2 style={{ marginBottom: '16px', fontSize: '20px', fontWeight: 600 }}>Upgrade de Plano</h2>
        <p style={{ marginBottom: '24px', color: theme.colors.textMuted, fontSize: '14px' }}>
          Expanda sua capacidade e desbloqueie recursos exclusivos fazendo upgrade do seu plano.
        </p>

        <PlanUpgrade
          onUpgradeClick={(planKey) => {
            // Salva o plano selecionado e redireciona para a página de assinatura
            sessionStorage.setItem('pendingPlanType', planKey);
            window.location.href = '/subscription';
          }}
        />
      </div>
    </div>
  );
};
